/**
 * Cortex Resolve — AI observe theatre for Ops map (Soft≠Live).
 *
 * Not dashed purple lines: ranked arc corridors, attention cones,
 * constellation graph, and flow sparks along proposed paths.
 * Never claims Live hardware actuation.
 */
function haversineM(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return 1e12;
  const R = 6371000;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[1] - a[1]);
  const dLng = toR(b[0] - a[0]);
  const lat1 = toR(a[1]);
  const lat2 = toR(b[1]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}

function deviceLl(d) {
  if (!d) return null;
  if (Array.isArray(d.ll) && d.ll.length >= 2) return [Number(d.ll[0]), Number(d.ll[1])];
  const lat = d.map_lat ?? d.lat;
  const lng = d.map_lng ?? d.lng;
  if (lat == null || lng == null) return null;
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return [b, a];
}

function ringCentroid(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const p of ring) {
    if (!Array.isArray(p) || p.length < 2) continue;
    sx += Number(p[0]);
    sy += Number(p[1]);
    n += 1;
  }
  if (!n) return null;
  return [sx / n, sy / n];
}

/** Quadratic arc corridor — offset midpoint so paths don't stack. */
function arcCoords(from, to, bend = 0.18, rank = 0) {
  const mx = (from[0] + to[0]) / 2;
  const my = (from[1] + to[1]) / 2;
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy) || 1e-9;
  const nx = -dy / len;
  const ny = dx / len;
  const side = rank % 2 === 0 ? 1 : -1;
  const mag = bend * len * (1 + rank * 0.12) * side;
  const cx = mx + nx * mag;
  const cy = my + ny * mag;
  const pts = [];
  for (let i = 0; i <= 18; i++) {
    const t = i / 18;
    const u = 1 - t;
    pts.push([
      u * u * from[0] + 2 * u * t * cx + t * t * to[0],
      u * u * from[1] + 2 * u * t * cy + t * t * to[1],
    ]);
  }
  return pts;
}

function wedgePolygon(origin, target, halfDeg = 18, reachFrac = 0.72) {
  if (!origin || !target) return null;
  const dx = target[0] - origin[0];
  const dy = target[1] - origin[1];
  const ang = Math.atan2(dy, dx);
  const dist = Math.hypot(dx, dy) * reachFrac;
  const h = (halfDeg * Math.PI) / 180;
  const a1 = ang - h;
  const a2 = ang + h;
  return [
    origin,
    [origin[0] + Math.cos(a1) * dist, origin[1] + Math.sin(a1) * dist],
    [origin[0] + Math.cos(a2) * dist, origin[1] + Math.sin(a2) * dist],
    origin,
  ];
}

const RANK_COLORS = ["#FF6B5A", "#7CFFB2", "#6ED8FF", "#E8A86A"];

export function buildAiSimSignals({
  devices = [],
  alarms = [],
  ontology = null,
  now = Date.now(),
} = {}) {
  const openDev = new Set(
    (alarms || [])
      .filter((a) => a && (a.open || a.status === "open"))
      .map((a) => String(a.dev || a.device_id || "")),
  );
  const candIds = new Set(
    (((ontology || {}).ai_resolver || {}).candidates || [])
      .map((c) => String(c.resource_id || "").replace(/^device:/, ""))
      .filter(Boolean),
  );
  const features = [];
  for (const d of devices || []) {
    const id = String(d.id || d.device_id || "").trim();
    if (!id) continue;
    const ll = deviceLl(d);
    if (!ll) continue;
    const isQa = /^SN-QA/i.test(id);
    const hot = openDev.has(id);
    const picked = candIds.has(id);
    if (!isQa && !hot && !picked) continue;
    const kind = hot ? "alarm" : picked ? "observe" : "sim";
    features.push({
      type: "Feature",
      properties: {
        deviceId: id,
        kind,
        soft_ne_live: true,
        simulated: true,
        label: hot ? "Hot signal" : picked ? "Cortex lock" : "Probe",
        score: hot ? 92 : picked ? 78 : 44,
        t: now,
      },
      geometry: { type: "Point", coordinates: ll },
    });
  }
  return { type: "FeatureCollection", features };
}

function resolveTargets({ devices, alarms, ontology, maxRoutes }) {
  const candsRaw = (((ontology || {}).ai_resolver || {}).candidates || []).slice(0, 8);
  if (candsRaw.length) return candsRaw;
  const fromAlarm = (devices || [])
    .filter((d) => {
      const id = String(d.id || d.device_id || "");
      return (
        id &&
        (alarms || []).some(
          (a) => a && (a.open || a.status === "open") && String(a.dev || a.device_id) === id,
        )
      );
    })
    .slice(0, maxRoutes)
    .map((d) => ({ resource_id: `device:${d.id || d.device_id}`, score: 90 }));
  if (fromAlarm.length) return fromAlarm;
  return (devices || [])
    .filter((d) => /^SN-QA/i.test(String(d.id || "")))
    .slice(0, maxRoutes)
    .map((d) => ({ resource_id: `device:${d.id}`, score: 50 }));
}

/**
 * Ranked arc corridors + attention cones + constellation edges.
 */
export function buildAiCortexPack({
  devices = [],
  zones = [],
  fences = {},
  personnel = [],
  ontology = null,
  alarms = [],
  maxRoutes = 4,
} = {}) {
  const cands = resolveTargets({ devices, alarms, ontology, maxRoutes });
  const people = (personnel || [])
    .map((p) => {
      const ll = deviceLl(p) || (Array.isArray(p.ll) ? p.ll : null);
      if (!ll) return null;
      return { id: String(p.username || p.id || "responder"), ll, onCall: !!(p.on_call || p.onCall) };
    })
    .filter(Boolean);

  const routes = [];
  const cones = [];
  const graph = [];
  let n = 0;
  for (const c of cands) {
    if (n >= maxRoutes) break;
    const did = String(c.resource_id || "").replace(/^device:/, "");
    const dev = (devices || []).find((d) => String(d.id || d.device_id) === did);
    const to = deviceLl(dev);
    if (!to) continue;
    let from = null;
    let fromId = "zone";
    let best = 1e12;
    for (const p of people) {
      const dist = haversineM(p.ll, to);
      if (dist < best) {
        best = dist;
        from = p.ll;
        fromId = p.id;
      }
    }
    let zoneLl = null;
    let zoneId = "";
    const zHit = (zones || []).find((z) => (z.deviceIds || []).includes(did));
    if (zHit) {
      zoneId = String(zHit.id || zHit.label || zHit.zone_id || "");
      const ring = fences[zoneId] || fences[zHit.label];
      zoneLl = ringCentroid(ring) || (Array.isArray(zHit.ll) ? zHit.ll : null);
    }
    if (!from) {
      from = zoneLl;
      fromId = zoneId || "volume";
    }
    if (!from) continue;
    const meters = Math.round(haversineM(from, to));
    const rank = n + 1;
    const color = RANK_COLORS[(rank - 1) % RANK_COLORS.length];
    const coords = arcCoords(from, to, 0.2, n);
    routes.push({
      type: "Feature",
      properties: {
        deviceId: did,
        fromId,
        meters,
        rank,
        score: Number(c.score) || Math.max(40, 100 - Math.round(meters / 8)),
        soft_ne_live: true,
        simulated: true,
        profile: "cortex",
        color,
        label: `#${rank} · ${fromId} → ${did} · ${meters}m`,
      },
      geometry: { type: "LineString", coordinates: coords },
    });
    const wedge = wedgePolygon(to, from, 16 + n * 2, 0.55);
    if (wedge) {
      cones.push({
        type: "Feature",
        properties: {
          deviceId: did,
          rank,
          color,
          soft_ne_live: true,
          simulated: true,
        },
        geometry: { type: "Polygon", coordinates: [wedge] },
      });
    }
    if (zoneLl) {
      graph.push({
        type: "Feature",
        properties: { kind: "zone-cam", color: "#7CFFB2", soft_ne_live: true },
        geometry: { type: "LineString", coordinates: [zoneLl, to] },
      });
    }
    graph.push({
      type: "Feature",
      properties: { kind: "unit-cam", color: "#6ED8FF", soft_ne_live: true },
      geometry: { type: "LineString", coordinates: [from, to] },
    });
    n += 1;
  }
  return {
    routes: { type: "FeatureCollection", features: routes },
    cones: { type: "FeatureCollection", features: cones },
    graph: { type: "FeatureCollection", features: graph },
  };
}

/** Flow sparks along ranked arcs — call each animation frame with phase 0..1. */
export function buildAiFlowSparks(routesFc, phase = 0) {
  const features = [];
  const routes = (routesFc && routesFc.features) || [];
  for (const r of routes) {
    const coords = r.geometry && r.geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;
    const rank = Number((r.properties && r.properties.rank) || 1);
    const color = (r.properties && r.properties.color) || RANK_COLORS[0];
    for (let k = 0; k < 3; k++) {
      const t = (phase + k * 0.28 + rank * 0.07) % 1;
      const idx = Math.min(coords.length - 2, Math.floor(t * (coords.length - 1)));
      const local = t * (coords.length - 1) - idx;
      const a = coords[idx];
      const b = coords[idx + 1];
      features.push({
        type: "Feature",
        properties: {
          rank,
          color,
          soft_ne_live: true,
          simulated: true,
          spark: k,
        },
        geometry: {
          type: "Point",
          coordinates: [a[0] + (b[0] - a[0]) * local, a[1] + (b[1] - a[1]) * local],
        },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

export function buildAiDispatchRoutes(opts) {
  return buildAiCortexPack(opts).routes;
}

export function aiLoopSummary(signalsFc, routesFc, extra = {}) {
  const sn = (signalsFc && signalsFc.features && signalsFc.features.length) || 0;
  const rn = (routesFc && routesFc.features && routesFc.features.length) || 0;
  const top = routesFc && routesFc.features && routesFc.features[0];
  const topLabel = top && top.properties ? top.properties.label : "";
  let stage = "idle";
  if (sn && !rn) stage = "observe";
  else if (sn && rn) stage = "route";
  else if (rn) stage = "ready";
  if (sn && rn) stage = "resolve";
  return {
    soft_ne_live: true,
    signals: sn,
    routes: rn,
    stage,
    topLabel,
    stages: ["observe", "score", "route", "ready"],
    line:
      sn || rn
        ? `Cortex · ${sn} lock${sn === 1 ? "" : "s"} · ${rn} corridor${rn === 1 ? "" : "s"}`
        : "Cortex · waiting for map geometry",
    ...extra,
  };
}

export default {
  buildAiSimSignals,
  buildAiDispatchRoutes,
  buildAiCortexPack,
  buildAiFlowSparks,
  aiLoopSummary,
};
